import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiUser, FiMail, FiPhone, FiMapPin, FiEdit, FiUpload, FiLock, FiEye, FiEyeOff, FiArrowRight, FiCheck, FiTrash2, FiPlus, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { userApi } from '../../api/userApi';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('personal');
  const [user, setUser] = useState({
    name: '',
    email: '',
    phone: '',
    avatar: null,
    addresses: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [passwordStep, setPasswordStep] = useState(1);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Add new states for modals and form
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [addressToDeactivate, setAddressToDeactivate] = useState(null);
  const [addressForm, setAddressForm] = useState({
    address_type: 'home',
    first_name: '',
    last_name: '',
    company_name: '',
    country: 'India',
    street_address_1: '',
    street_address_2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    order_notes: '',
    is_default: false
  });
  const [isEditing, setIsEditing] = useState(false);

  const addressTypes = ['home', 'office', 'other'];

  const handleCancelAddress = () => {
    resetAddressForm();
    setShowAddressModal(false);
  };

  const resetAddressForm = () => {
    setAddressForm({
      address_type: 'home',
      first_name: '',
      last_name: '',
      company_name: '',
      country: 'India',
      street_address_1: '',
      street_address_2: '',
      city: '',
      state: '',
      pincode: '',
      phone: '',
      email: '',
      order_notes: '',
      is_default: false
    });
    setIsEditing(false);
    setFormErrors({});
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setUser(prev => ({ ...prev, avatar: reader.result }));
        toast.success('Profile picture updated successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  // Fetch user profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('Please login to view your profile');
          // Optionally redirect to login page
          // navigate('/login');
          return;
        }

        // Fetch both profile and addresses
        const [profileData, addressesData] = await Promise.all([
          userApi.getProfile(),
          userApi.getAddresses()
        ]);
        
        setUser({
          name: profileData.full_name,
          email: profileData.email,
          phone: profileData.phone_number,
          avatar: null,
          addresses: addressesData || []
        });
      } catch (error) {
        console.error('Error fetching profile data:', error);
        if (error.message.includes('401')) {
          toast.error('Session expired. Please login again');
          // Optionally redirect to login page
          // navigate('/login');
        } else {
          toast.error('Failed to load profile data');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const handleSavePersonalInfo = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await userApi.updateProfile({
        full_name: user.name.trim(),
        email: user.email,
        phone_number: user.phone
      });

      toast.success('Personal information updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAddress = () => {
    resetAddressForm();
    setShowAddressModal(true);
  };

  const handleEditAddress = (address) => {
    setAddressForm({
      id: address.id,
      address_type: address.address_type.toLowerCase(),
      first_name: address.first_name,
      last_name: address.last_name,
      company_name: address.company_name || '',
      country: address.country || 'India',
      street_address_1: address.street_address_1,
      street_address_2: address.street_address_2 || '',
      city: address.city,
      state: address.state,
      pincode: address.pincode,
      phone: address.phone,
      email: address.email,
      order_notes: address.order_notes || '',
      is_default: address.is_default || false
    });
    setIsEditing(true);
    setShowAddressModal(true);
  };

  const handleDeactivateAddressClick = (address) => {
    setAddressToDeactivate(address);
    setShowDeactivateModal(true);
  };

  const handleDeactivateAddress = async () => {
    try {
      await userApi.toggleAddressActive(addressToDeactivate.id);
      
      // Update local state to reflect the status change
      setUser(prev => ({
        ...prev,
        addresses: prev.addresses.map(addr => 
          addr.id === addressToDeactivate.id 
            ? { ...addr, is_active: !addr.is_active }
            : addr
        )
      }));
      
      toast.success('Address status updated successfully');
    } catch (error) {
      console.error('Error updating address status:', error);
      toast.error('Failed to update address status');
    } finally {
      setShowDeactivateModal(false);
      setAddressToDeactivate(null);
    }
  };

  const validateAddressForm = () => {
    const errors = {};
    
    if (!addressForm.first_name) errors.first_name = 'First name is required';
    if (!addressForm.last_name) errors.last_name = 'Last name is required';
    if (!addressForm.street_address_1) errors.street_address_1 = 'Street address is required';
    if (!addressForm.city) errors.city = 'City is required';
    if (!addressForm.state) errors.state = 'State is required';
    if (!addressForm.pincode) errors.pincode = 'Pincode is required';
    if (!addressForm.phone) errors.phone = 'Phone is required';
    if (!addressForm.email) errors.email = 'Email is required';
    
    return errors;
  };

  const handleAddressFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateAddressForm();
    if (Object.keys(errors).length > 0) {
      // Show validation errors
      Object.values(errors).forEach(error => toast.error(error));
      return;
    }

    try {
      setIsSaving(true);
      
      if (isEditing) {
        // Update existing address
        const updatedAddress = await userApi.updateAddress(addressForm.id, {
          address_type: addressForm.address_type,
          first_name: addressForm.first_name,
          last_name: addressForm.last_name,
          company_name: addressForm.company_name,
          country: addressForm.country,
          street_address_1: addressForm.street_address_1,
          street_address_2: addressForm.street_address_2,
          city: addressForm.city,
          state: addressForm.state,
          pincode: addressForm.pincode,
          phone: addressForm.phone,
          email: addressForm.email,
          order_notes: addressForm.order_notes,
          is_default: addressForm.is_default
        });

        // Update the addresses list with the updated address
        setUser(prev => ({
          ...prev,
          addresses: prev.addresses.map(addr => 
            addr.id === addressForm.id ? updatedAddress : addr
          )
        }));
        
        toast.success('Address updated successfully');
      } else {
        // Add new address
        const newAddress = await userApi.addAddress(addressForm);
        setUser(prev => ({
          ...prev,
          addresses: [...prev.addresses, newAddress]
        }));
        toast.success('Address added successfully');
      }
      
      // Close modal and reset form
      setShowAddressModal(false);
      resetAddressForm();
    } catch (error) {
      console.error('Error submitting address:', error);
      toast.error(error.message || (isEditing ? 'Failed to update address' : 'Failed to add address'));
    } finally {
      setIsSaving(false);
    }
  };

  const verifyCurrentPassword = async () => {
    setIsVerifyingPassword(true);
    setPasswordErrors({});

    try {
      // Add your API call here to verify current password
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      if (passwordForm.currentPassword.length < 1) {
        setPasswordErrors(prev => ({
          ...prev,
          currentPassword: 'Please enter your current password'
        }));
        return;
      }
      
      setPasswordStep(2);
    } catch (error) {
      setPasswordErrors(prev => ({
        ...prev,
        currentPassword: 'Current password is incorrect'
      }));
    } finally {
      setIsVerifyingPassword(false);
    }
  };

  const validateAndSetNewPassword = () => {
    setPasswordErrors({});

    if (passwordForm.newPassword.length < 8) {
      setPasswordErrors(prev => ({
        ...prev,
        newPassword: 'Password must be at least 8 characters long'
      }));
      return;
    }

    setPasswordStep(3);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordErrors({});

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordErrors(prev => ({
        ...prev,
        confirmPassword: 'Passwords do not match'
      }));
      return;
    }

    try {
      // Add your API call here to change password
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      toast.success('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStep(1);
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  const renderPersonalInfo = () => (
    <form onSubmit={handleSavePersonalInfo} className="space-y-8">
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <>
          {/* Personal Details Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={user.email}
                onChange={(e) => setUser(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                value={user.phone}
                onChange={(e) => setUser(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-[#2C3E50] text-white rounded-lg hover:bg-[#34495E] focus:outline-none focus:ring-2 focus:ring-[#2C3E50] focus:ring-offset-2 transition-colors disabled:opacity-50 flex items-center"
            >
              {isSaving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </>
      )}
    </form>
  );

  const renderAddresses = () => (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-semibold text-gray-900">Saved Addresses</h3>
          <p className="text-gray-500 mt-1">Manage your delivery locations</p>
        </div>
        <button
          onClick={handleAddAddress}
          className="inline-flex items-center px-5 py-2.5 bg-[#2C3E50] text-white rounded-xl hover:bg-[#34495E] transition-all duration-300 shadow-sm hover:shadow group"
        >
          <FiMapPin className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
          Add New Address
        </button>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        /* Addresses Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {user.addresses && user.addresses.length > 0 ? (
            user.addresses.map(address => (
              <div
                key={address.id}
                className="relative bg-white rounded-xl border border-gray-100 p-6 hover:shadow-lg transition-all duration-300"
              >
                {/* Address Type Badge */}
                <div className="absolute top-4 right-4 flex gap-2">
                  <span className="px-3 py-1 bg-gray-50 text-gray-600 rounded-full text-sm font-medium capitalize">
                    {address.address_type}
                  </span>
                </div>

                {/* Address Content */}
                <div className="mt-4 space-y-4">
                  <div className="space-y-2 text-gray-600">
                    <p className="font-medium text-gray-900">
                      {address.first_name} {address.last_name}
                    </p>
                    {address.company_name && (
                      <p className="text-gray-600">{address.company_name}</p>
                    )}
                    <p>{address.street_address_1}</p>
                    {address.street_address_2 && (
                      <p>{address.street_address_2}</p>
                    )}
                    <p>{address.city}, {address.state}</p>
                    <p>PIN: {address.pincode}</p>
                    <p>Phone: {address.phone}</p>
                    <p>Email: {address.email}</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-4 border-t border-gray-50 flex gap-3">
                    <button
                      onClick={() => handleEditAddress(address)}
                      className="flex items-center px-4 py-2 text-sm text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <FiEdit className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                    {address.is_active ? (
                      <button
                        onClick={() => handleDeactivateAddressClick(address)}
                        className="flex items-center px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        <FiTrash2 className="h-4 w-4 mr-2" />
                        Delete
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeactivateAddressClick(address)}
                        className="flex items-center px-4 py-2 text-sm text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        <FiCheck className="h-4 w-4 mr-2" />
                        Activate
                      </button>
                    )}
                  </div>
                </div>

                {/* Default Address Badge */}
                {address.is_default && (
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium flex items-center">
                      <FiCheck className="h-3 w-3 mr-1" />
                      Default
                    </span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center p-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <FiMapPin className="h-12 w-12 text-gray-400 mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No addresses saved yet</h4>
              <p className="text-gray-500 text-center mb-4">Add your first delivery address to get started</p>
              <button
                onClick={handleAddAddress}
                className="inline-flex items-center px-4 py-2 bg-[#2C3E50] text-white rounded-lg hover:bg-[#34495E] transition-colors"
              >
                <FiPlus className="mr-2 h-4 w-4" />
                Add New Address
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderChangePassword = () => (
    <div className="max-w-md mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`flex items-center justify-center w-8 h-8 rounded-full border-2 
                ${passwordStep >= step 
                  ? 'border-[#2C3E50] bg-[#2C3E50] text-white'
                  : 'border-gray-300 bg-white text-gray-400'
              }`}
            >
              {passwordStep > step ? (
                <FiCheck className="w-5 h-5" />
              ) : (
                <span>{step}</span>
              )}
            </div>
          ))}
        </div>
        <div className="relative">
          <div className="absolute top-0 left-0 h-1 bg-gray-200 w-full rounded">
            <div
              className="absolute top-0 left-0 h-1 bg-[#2C3E50] rounded transition-all duration-300"
              style={{ width: `${((passwordStep - 1) / 2) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step 1: Current Password */}
      {passwordStep === 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Verify Current Password</h3>
          <p className="text-sm text-gray-500">
            Please enter your current password to continue
          </p>
          <div className="relative">
            <input
              type={showCurrentPassword ? "text" : "password"}
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm(prev => ({
                ...prev,
                currentPassword: e.target.value
              }))}
              className={`w-full px-4 py-2 pr-10 rounded-lg border focus:ring-2 focus:ring-[#2C3E50] focus:border-transparent ${
                passwordErrors.currentPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Current Password"
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showCurrentPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
          {passwordErrors.currentPassword && (
            <p className="text-sm text-red-500">{passwordErrors.currentPassword}</p>
          )}
          <button
            onClick={verifyCurrentPassword}
            disabled={isVerifyingPassword}
            className="w-full px-6 py-2 bg-[#2C3E50] text-white rounded-lg hover:bg-[#34495E] focus:outline-none focus:ring-2 focus:ring-[#2C3E50] focus:ring-offset-2 transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {isVerifyingPassword ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                Continue
                <FiArrowRight className="ml-2" />
              </>
            )}
          </button>
        </div>
      )}

      {/* Step 2: New Password */}
      {passwordStep === 2 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Set New Password</h3>
          <p className="text-sm text-gray-500">
            Choose a strong password with at least 8 characters
          </p>
          <div className="relative">
            <input
              type={showNewPassword ? "text" : "password"}
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({
                ...prev,
                newPassword: e.target.value
              }))}
              className={`w-full px-4 py-2 pr-10 rounded-lg border focus:ring-2 focus:ring-[#2C3E50] focus:border-transparent ${
                passwordErrors.newPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="New Password"
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showNewPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
          {passwordErrors.newPassword && (
            <p className="text-sm text-red-500">{passwordErrors.newPassword}</p>
          )}
          <div className="flex space-x-3">
            <button
              onClick={() => setPasswordStep(1)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#2C3E50] focus:ring-offset-2 transition-colors"
            >
              Back
            </button>
            <button
              onClick={validateAndSetNewPassword}
              className="flex-1 px-6 py-2 bg-[#2C3E50] text-white rounded-lg hover:bg-[#34495E] focus:outline-none focus:ring-2 focus:ring-[#2C3E50] focus:ring-offset-2 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm Password */}
      {passwordStep === 3 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Confirm New Password</h3>
          <p className="text-sm text-gray-500">
            Please confirm your new password
          </p>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({
                ...prev,
                confirmPassword: e.target.value
              }))}
              className={`w-full px-4 py-2 pr-10 rounded-lg border focus:ring-2 focus:ring-[#2C3E50] focus:border-transparent ${
                passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Confirm New Password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
          {passwordErrors.confirmPassword && (
            <p className="text-sm text-red-500">{passwordErrors.confirmPassword}</p>
          )}
          <div className="flex space-x-3">
            <button
              onClick={() => setPasswordStep(2)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#2C3E50] focus:ring-offset-2 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handlePasswordChange}
              className="flex-1 px-6 py-2 bg-[#2C3E50] text-white rounded-lg hover:bg-[#34495E] focus:outline-none focus:ring-2 focus:ring-[#2C3E50] focus:ring-offset-2 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>My Profile | Panchali Boutique</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage your personal information and addresses
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[240px,1fr] gap-8">
          {/* Sidebar Navigation */}
          <nav className="space-y-1">
            {[
              { id: 'personal', name: 'Personal Information', icon: FiUser },
              { id: 'addresses', name: 'Addresses', icon: FiMapPin },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === item.id
                    ? 'bg-[#2C3E50] text-white'
                    : 'text-gray-600 hover:bg-[#34495E] hover:text-white'
                }`}
              >
                <item.icon className={`mr-3 h-5 w-5 ${
                  activeTab === item.id ? 'text-white' : 'text-gray-400'
                }`} />
                {item.name}
              </button>
            ))}
          </nav>

          {/* Main Content */}
          <main className="bg-white shadow-sm rounded-lg p-8">
            {activeTab === 'personal' && renderPersonalInfo()}
            {activeTab === 'addresses' && renderAddresses()}
          </main>
        </div>
      </div>

      {/* Address Form Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl my-8">
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isEditing ? 'Edit Address' : 'Add New Address'}
                </h2>
                <button
                  onClick={handleCancelAddress}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <FiX className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable Content */}
            <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <form onSubmit={handleAddressSubmit} className="space-y-6">
                {/* Address Type and Default Selection */}
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address Type</label>
                    <div className="flex flex-wrap gap-3">
                      {addressTypes.map(type => (
                        <label
                          key={type}
                          className={`flex-1 min-w-[100px] flex items-center justify-center px-4 py-2 border rounded-lg cursor-pointer transition-all ${
                            addressForm.address_type === type
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="address_type"
                            value={type}
                            checked={addressForm.address_type === type}
                            onChange={handleAddressFormChange}
                            className="sr-only"
                          />
                          <span className="capitalize">{type}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Personal Information */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-medium text-gray-900">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                      <input
                        type="text"
                        name="first_name"
                        value={addressForm.first_name}
                        onChange={handleAddressFormChange}
                        className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                      <input
                        type="text"
                        name="last_name"
                        value={addressForm.last_name}
                        onChange={handleAddressFormChange}
                        className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={addressForm.email}
                        onChange={handleAddressFormChange}
                        className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={addressForm.phone}
                        onChange={handleAddressFormChange}
                        className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Address Details */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-medium text-gray-900">Address Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Name (Optional)</label>
                      <input
                        type="text"
                        name="company_name"
                        value={addressForm.company_name}
                        onChange={handleAddressFormChange}
                        className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
                      <input
                        type="text"
                        name="street_address_1"
                        value={addressForm.street_address_1}
                        onChange={handleAddressFormChange}
                        placeholder="House number and street name"
                        className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="street_address_2"
                        value={addressForm.street_address_2}
                        onChange={handleAddressFormChange}
                        placeholder="Apartment, suite, unit, etc. (optional)"
                        className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                        <input
                          type="text"
                          name="city"
                          value={addressForm.city}
                          onChange={handleAddressFormChange}
                          className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                        <input
                          type="text"
                          name="state"
                          value={addressForm.state}
                          onChange={handleAddressFormChange}
                          className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code *</label>
                        <input
                          type="text"
                          name="pincode"
                          value={addressForm.pincode}
                          onChange={handleAddressFormChange}
                          className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  <h3 className="font-medium text-gray-900">Additional Information</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Order Notes (Optional)</label>
                    <textarea
                      name="order_notes"
                      value={addressForm.order_notes}
                      onChange={handleAddressFormChange}
                      rows="3"
                      className="w-full px-4 py-2.5 rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Notes about your order, e.g. special notes for delivery"
                    />
                  </div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      name="is_default"
                      checked={addressForm.is_default}
                      onChange={handleAddressFormChange}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">Set as default address</span>
                  </label>
                </div>
              </form>
            </div>

            {/* Modal Footer - Sticky Bottom */}
            <div className="p-4 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-xl">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelAddress}
                  className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  onClick={handleAddressSubmit}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  {isEditing ? 'Update Address' : 'Save Address'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate Confirmation Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">{addressToDeactivate?.is_active ? 'Deactivate' : 'Activate'} Address</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to {addressToDeactivate?.is_active ? 'deactivate' : 'activate'} this address?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setAddressToDeactivate(null);
                }}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivateAddress}
                className={`px-4 py-2 text-white rounded-lg hover:bg-red-700 transition-colors ${
                  addressToDeactivate?.is_active 
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {addressToDeactivate?.is_active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Profile;


























